import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clipboard,
  Clock3,
  Info,
  KeyRound,
  Link2,
  LoaderCircle,
  Mail,
  MessageSquare,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '../../lib/api';
import { appPath } from '../../lib/navigation';
import { queryKeys } from '../../lib/queryClient';
import type {
  AdminCapabilityKey,
  AdminCapabilityState,
  AdminHealth,
  AdminHealthCheck,
  AdminInvite,
  AdminUsage,
  AdminUsageUser,
  AdminUser,
  AuthenticatedUser,
} from '../../types';
import {
  createAdminUser,
  deleteAdminUser,
  getAdminHealth,
  getAdminSignupCode,
  getAdminUsage,
  getAdminUsers,
  grantAdminZoom,
  regenerateAdminSignupCode,
  resetAdminUserPassword,
  resetUsageCycle,
  runAdminHealth,
  setRecallLimit,
  updateAdminUser,
} from './adminApi';
import { sendNotification } from './shellApi';
import { ModulePage } from './ModulePage';

interface AdminConsoleProps {
  user: AuthenticatedUser;
}

interface StatusMessage {
  tone: 'ok' | 'error';
  text: string;
}

const capabilityOptions: Array<{ key: AdminCapabilityKey; label: string; short: string }> = [
  { key: 'can_moderate', label: 'Moderation', short: 'Mod' },
  { key: 'can_stats', label: 'Stats', short: 'Stats' },
  { key: 'can_data', label: 'Data downloads', short: 'Data' },
  { key: 'can_team', label: 'Team Status', short: 'Team' },
  { key: 'can_display', label: 'TV Display', short: 'TV' },
  { key: 'can_av', label: 'AV Control', short: 'AV' },
  { key: 'can_events', label: 'Event Setup', short: 'Events' },
  { key: 'is_admin', label: 'Admin', short: 'Admin' },
];

const emptyCapabilities: AdminCapabilityState = {
  can_moderate: false,
  can_stats: false,
  can_data: false,
  can_team: false,
  can_display: false,
  can_av: false,
  can_events: false,
  is_admin: false,
};

export function AdminConsole({ user }: AdminConsoleProps) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [usageMessage, setUsageMessage] = useState<AdminUsageUser | null>(null);

  const users = useQuery({ queryKey: queryKeys.adminUsers, queryFn: ({ signal }) => getAdminUsers(signal), refetchInterval: 30_000 });
  const health = useQuery({ queryKey: queryKeys.adminHealth, queryFn: ({ signal }) => getAdminHealth(signal), staleTime: 60_000 });
  const usage = useQuery({ queryKey: queryKeys.adminUsage, queryFn: ({ signal }) => getAdminUsage(signal), refetchInterval: 30_000 });
  const signupCode = useQuery({ queryKey: queryKeys.adminSignupCode, queryFn: ({ signal }) => getAdminSignupCode(signal), staleTime: 60_000 });

  const approved = (users.data ?? []).filter(item => item.approved);
  const pending = (users.data ?? []).filter(item => !item.approved);
  const refreshUsers = () => queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });

  return (
    <ModulePage
      id="admin-title"
      title="Admin"
      className="admin-console"
      innerClassName="admin-console__inner"
      meta={<><span className="admin-count">{approved.length} people</span>{pending.length ? <span className="admin-pending-count">{pending.length} pending</span> : null}</>}
      actions={<button className="icon-button admin-refresh" type="button" title="Refresh admin data" aria-label="Refresh admin data" disabled={users.isFetching || usage.isFetching} onClick={() => void Promise.all([users.refetch(), usage.refetch(), health.refetch(), signupCode.refetch()])}><RefreshCw className={users.isFetching || usage.isFetching ? 'spin' : ''} size={17} /></button>}
    >
      {status ? <div className={`admin-feedback ${status.tone}`} role={status.tone === 'error' ? 'alert' : 'status'}><span>{status.text}</span><button className="icon-button" type="button" title="Dismiss status" aria-label="Dismiss status" onClick={() => setStatus(null)}><X size={14} /></button></div> : null}

      {users.isError ? <AdminLoadError error={users.error} onRetry={() => void users.refetch()} /> : (
        <div className="admin-layout">
          <HealthPanel health={health.data} loading={health.isPending} error={health.error} onStatus={setStatus} />
          {pending.length ? <PendingPanel users={pending} onChanged={() => void refreshUsers()} onStatus={setStatus} /> : null}
          <div className="admin-two-column">
            <AddPersonPanel onChanged={() => void refreshUsers()} onStatus={setStatus} />
            <SignupCodePanel code={signupCode.data?.code} loading={signupCode.isPending} error={signupCode.error} onChanged={code => queryClient.setQueryData(queryKeys.adminSignupCode, { code })} onStatus={setStatus} />
          </div>
          <UsagePanel data={usage.data} loading={usage.isPending} error={usage.error} onChanged={() => void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsage })} onMessage={setUsageMessage} onStatus={setStatus} />
          <PeoplePanel users={approved} currentUserId={user.id} onEdit={setEditUser} />
        </div>
      )}

      {editUser ? <EditUserDialog user={editUser} currentUserId={user.id} onClose={() => setEditUser(null)} onChanged={() => { setEditUser(null); void refreshUsers(); }} onStatus={setStatus} /> : null}
      {usageMessage ? <UsageMessageDialog user={usageMessage} onClose={() => setUsageMessage(null)} onSent={() => { setUsageMessage(null); setStatus({ tone: 'ok', text: `Message sent to ${usageMessage.name}.` }); }} /> : null}
    </ModulePage>
  );
}

function AdminLoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return <div className="native-module__state native-module__state--error" role="alert"><AlertCircle size={23} /><strong>Admin data could not be loaded</strong><span>{errorMessage(error, 'Try refreshing the workspace.')}</span><button className="secondary-button" type="button" onClick={onRetry}>Try again</button></div>;
}

function HealthPanel({ health, loading, error, onStatus }: { health: AdminHealth | undefined; loading: boolean; error: unknown; onStatus: (status: StatusMessage) => void }) {
  const queryClient = useQueryClient();
  const run = useMutation({
    mutationFn: runAdminHealth,
    onSuccess: result => { queryClient.setQueryData(queryKeys.adminHealth, result); onStatus({ tone: result.ok ? 'ok' : 'error', text: result.ok ? 'Health check completed. All configured systems are healthy.' : 'Health check completed with problems.' }); },
    onError: failure => onStatus({ tone: 'error', text: errorMessage(failure, 'The health check could not run.') }),
  });
  const score = healthScore(health);
  return (
    <section className="admin-panel health-panel" aria-labelledby="health-title">
      <header><div><Activity size={17} /><h2 id="health-title">System health</h2></div>{health?.ranAt ? <time dateTime={new Date(health.ranAt).toISOString()}>{formatDateTime(health.ranAt)}</time> : null}</header>
      {loading ? <div className="admin-panel__loading"><LoaderCircle className="spin" size={18} />Checking system health</div> : null}
      {error ? <div className="admin-panel__error"><AlertCircle size={16} /><span>{errorMessage(error, 'Health status is unavailable.')}</span></div> : null}
      {health ? <div className="health-content"><div className={`health-score ${score >= 80 ? 'ok' : score >= 50 ? 'warn' : 'fail'}`}><strong>{score}%</strong><span>{score === 100 ? 'All systems go' : score >= 80 ? 'Healthy' : score >= 50 ? 'Degraded' : 'Needs attention'}</span><div aria-hidden="true"><i style={{ width: `${score}%` }} /></div></div><div className="health-checks">{health.checks.map(check => <HealthCheckRow check={check} key={check.key} />)}</div></div> : null}
      <button className="secondary-button health-run" type="button" disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />}Run check now</button>
    </section>
  );
}

function HealthCheckRow({ check }: { check: AdminHealthCheck }) {
  const Icon = check.status === 'ok' ? CheckCircle2 : check.status === 'fail' ? XCircle : check.status === 'warn' ? AlertTriangle : Info;
  return <div className={`health-check ${check.status}`}><Icon size={15} /><strong>{check.label}</strong><span>{check.detail}</span></div>;
}

function AddPersonPanel({ onChanged, onStatus }: { onChanged: () => void; onStatus: (status: StatusMessage) => void }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [org, setOrg] = useState(true);
  const [caps, setCaps] = useState<AdminCapabilityState>(emptyCapabilities);
  const [invite, setInvite] = useState<AdminInvite | null>(null);
  const add = useMutation({
    mutationFn: createAdminUser,
    onSuccess: result => {
      setInvite(result);
      setEmail(''); setUsername(''); setPassword(''); setOrg(true); setCaps(emptyCapabilities);
      onStatus({ tone: 'ok', text: `${result.login} was added.` });
      onChanged();
    },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The person could not be added.') }),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!(email.trim() || username.trim()) || !password) {
      onStatus({ tone: 'error', text: 'Email or username and a temporary password are required.' });
      return;
    }
    add.mutate({ email: email.trim(), username: username.trim(), password, org, ...caps });
  }

  return (
    <section className="admin-panel add-person-panel" aria-labelledby="add-person-title">
      <header><div><UserPlus size={17} /><h2 id="add-person-title">Add a person</h2></div></header>
      <form onSubmit={submit}>
        <div className="admin-form-grid"><label><span>Email</span><input type="email" autoComplete="off" placeholder="name@company.com" value={email} onChange={event => setEmail(event.target.value)} /></label><label><span>Temporary password</span><input type="text" autoComplete="off" placeholder="Share securely" value={password} onChange={event => setPassword(event.target.value)} /></label></div>
        <div className="admin-form-grid"><label><span>Username <small>optional</small></span><input autoComplete="off" placeholder="Used only without email" value={username} onChange={event => setUsername(event.target.value)} /></label><label className="admin-checkbox admin-org-check"><input type="checkbox" checked={org} onChange={event => setOrg(event.target.checked)} /><span>Part of my organization</span></label></div>
        <div className="admin-cap-header"><span>Module access</span><button className="secondary-button" type="button" onClick={() => setCaps({ ...emptyCapabilities, can_moderate: true })}>Responder</button><button className="secondary-button" type="button" onClick={() => setCaps(emptyCapabilities)}>Clear</button></div>
        <CapabilityGrid value={caps} onChange={setCaps} />
        <button className="primary-button admin-add-submit" type="submit" disabled={add.isPending}>{add.isPending ? <LoaderCircle className="spin" size={15} /> : <Plus size={15} />}Add person</button>
      </form>
      {invite ? <InviteBox invite={invite} /> : null}
    </section>
  );
}

function InviteBox({ invite }: { invite: AdminInvite }) {
  const [copied, setCopied] = useState(false);
  const text = inviteText(invite);
  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }
  return <div className="admin-invite"><strong>Invite ready</strong><textarea readOnly rows={7} value={text} aria-label="New user invite" /><button className="secondary-button" type="button" onClick={() => void copy()}>{copied ? <Check size={14} /> : <Clipboard size={14} />}{copied ? 'Copied' : 'Copy invite'}</button></div>;
}

function SignupCodePanel({ code, loading, error, onChanged, onStatus }: { code: string | undefined; loading: boolean; error: unknown; onChanged: (code: string) => void; onStatus: (status: StatusMessage) => void }) {
  const [copied, setCopied] = useState(false);
  const regenerate = useMutation({
    mutationFn: regenerateAdminSignupCode,
    onSuccess: result => { onChanged(result.code); onStatus({ tone: 'ok', text: 'A new team signup code was generated.' }); },
    onError: failure => onStatus({ tone: 'error', text: errorMessage(failure, 'The signup code could not be regenerated.') }),
  });
  async function copy() {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }
  return (
    <section className="admin-panel signup-code-panel" aria-labelledby="signup-code-title">
      <header><div><KeyRound size={17} /><h2 id="signup-code-title">Team signup code</h2></div></header>
      <p>Anyone who signs up with this code joins your organization as a Responder.</p>
      {loading ? <div className="admin-panel__loading"><LoaderCircle className="spin" size={17} />Loading code</div> : null}
      {error ? <div className="admin-panel__error"><AlertCircle size={15} /><span>{errorMessage(error, 'Code unavailable.')}</span></div> : null}
      {code ? <code>{code}</code> : null}
      <div className="signup-code-actions"><button className="secondary-button" type="button" disabled={!code} onClick={() => void copy()}>{copied ? <Check size={14} /> : <Clipboard size={14} />}{copied ? 'Copied' : 'Copy'}</button><button className="danger-button" type="button" disabled={regenerate.isPending} onClick={() => { if (window.confirm('Regenerate the team code? The current code will stop working immediately.')) regenerate.mutate(); }}>{regenerate.isPending ? <LoaderCircle className="spin" size={14} /> : <RefreshCw size={14} />}Regenerate</button></div>
    </section>
  );
}

function PendingPanel({ users, onChanged, onStatus }: { users: AdminUser[]; onChanged: () => void; onStatus: (status: StatusMessage) => void }) {
  return (
    <section className="admin-panel pending-panel" aria-labelledby="pending-title">
      <header><div><Clock3 size={17} /><h2 id="pending-title">Pending requests</h2><span>{users.length}</span></div></header>
      <div className="admin-table-wrap"><table><caption>Accounts waiting for administrator approval</caption><thead><tr><th>User</th><th>Grant access</th><th>Actions</th></tr></thead><tbody>{users.map(user => <PendingRow key={user.id} user={user} onChanged={onChanged} onStatus={onStatus} />)}</tbody></table></div>
    </section>
  );
}

function PendingRow({ user, onChanged, onStatus }: { user: AdminUser; onChanged: () => void; onStatus: (status: StatusMessage) => void }) {
  const [caps, setCaps] = useState<AdminCapabilityState>(emptyCapabilities);
  const approve = useMutation({
    mutationFn: async (responder: boolean) => {
      const access = responder ? { ...emptyCapabilities, can_moderate: true } : caps;
      await updateAdminUser(user.id, access, true);
      if (responder) await grantAdminZoom(user.id, true);
    },
    onSuccess: (_result, responder) => { onStatus({ tone: 'ok', text: `${displayName(user)} approved${responder ? ' as a Responder using your Zoom.' : '.'}` }); onChanged(); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The account could not be approved.') }),
  });
  const reject = useMutation({
    mutationFn: () => deleteAdminUser(user.id),
    onSuccess: () => { onStatus({ tone: 'ok', text: `${displayName(user)} was rejected.` }); onChanged(); },
    onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The request could not be rejected.') }),
  });
  return <tr><td><UserIdentity user={user} badge="Pending" /></td><td><CapabilityGrid compact value={caps} onChange={setCaps} /></td><td><div className="admin-row-actions"><button className="primary-button" type="button" disabled={approve.isPending} title="Moderation only and use your Zoom account" onClick={() => approve.mutate(true)}><ShieldCheck size={13} />Responder</button><button className="secondary-button" type="button" disabled={approve.isPending} onClick={() => approve.mutate(false)}><UserCheck size={13} />Approve custom</button><button className="danger-button" type="button" disabled={reject.isPending} onClick={() => { if (window.confirm(`Reject and delete ${user.username}'s request?`)) reject.mutate(); }}><Trash2 size={13} />Reject</button></div></td></tr>;
}

function UsagePanel({ data, loading, error, onChanged, onMessage, onStatus }: { data: AdminUsage | undefined; loading: boolean; error: unknown; onChanged: () => void; onMessage: (user: AdminUsageUser) => void; onStatus: (status: StatusMessage) => void }) {
  const [limit, setLimit] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const limitValue = limit ?? (data ? String(data.limit) : '');
  const saveLimit = useMutation({
    mutationFn: (value: number) => setRecallLimit(value),
    onSuccess: result => { setLimit(String(result.limit)); onStatus({ tone: 'ok', text: 'Recall usage alert limit saved.' }); onChanged(); },
    onError: failure => onStatus({ tone: 'error', text: errorMessage(failure, 'The alert limit could not be saved.') }),
  });
  const reset = useMutation({
    mutationFn: resetUsageCycle,
    onSuccess: () => { onStatus({ tone: 'ok', text: 'Recall usage cycle reset.' }); onChanged(); },
    onError: failure => onStatus({ tone: 'error', text: errorMessage(failure, 'The usage cycle could not be reset.') }),
  });
  const rows = data?.users ?? [];
  const paging = paginate(rows, page, pageSize);
  return (
    <section className="admin-panel usage-panel" aria-labelledby="usage-title">
      <header><div><CircleDollarSign size={17} /><h2 id="usage-title">Recall bot usage</h2></div>{data ? <span>${data.ratePerHour.toFixed(2)} per bot-hour</span> : null}</header>
      <form className="usage-limit" onSubmit={event => { event.preventDefault(); const value = Number(limitValue); if (value > 0) saveLimit.mutate(value); else onStatus({ tone: 'error', text: 'Enter a positive alert limit.' }); }}><label><span>Alert limit</span><div><b>$</b><input aria-label="Recall usage alert limit" min="1" step="1" type="number" value={limitValue} onChange={event => setLimit(event.target.value)} /></div></label><button className="secondary-button" type="submit" disabled={saveLimit.isPending}><Save size={14} />Save limit</button></form>
      {loading ? <div className="admin-panel__loading"><LoaderCircle className="spin" size={18} />Loading usage</div> : null}
      {error ? <div className="admin-panel__error"><AlertCircle size={15} /><span>{errorMessage(error, 'Recall usage is unavailable.')}</span></div> : null}
      {data ? <><div className="admin-table-wrap"><table><caption>Recall bot usage by account</caption><thead><tr><th>#</th><th>Account</th><th>Usage this cycle</th><th>Actions</th></tr></thead><tbody>{paging.items.map((item, index) => <tr key={item.id}><td className="admin-row-number">{paging.start + index + 1}</td><td><div className="admin-user-identity"><strong>{item.name}{item.over ? <span className="admin-danger-badge">Over limit</span> : null}{item.live ? <span className="admin-live-badge">{item.live} live</span> : null}</strong><span>{item.email}</span></div></td><td><UsageBar user={item} limit={data.limit} /></td><td><div className="admin-row-actions"><button className="secondary-button" type="button" onClick={() => onMessage(item)}><MessageSquare size={13} />Message</button><button className="secondary-button" type="button" disabled={reset.isPending} onClick={() => { if (window.confirm(`Reset ${item.name}'s usage cycle to $0.00 now?`)) reset.mutate(item.id); }}><RotateCcw size={13} />Reset cycle</button></div></td></tr>)}</tbody></table></div><Pagination total={rows.length} page={paging.page} pageSize={pageSize} onPage={setPage} onPageSize={value => { setPageSize(value); setPage(0); }} /></> : null}
    </section>
  );
}

function UsageBar({ user, limit }: { user: AdminUsageUser; limit: number }) {
  const percent = Math.min(100, Math.round(user.cost / limit * 100));
  return <div className={`usage-bar ${user.over ? 'over' : percent >= 75 ? 'warn' : ''}`}><div><strong>${user.cost.toFixed(2)}</strong><span>of ${limit.toFixed(2)} · {user.hours.toFixed(1)}h</span></div><span aria-label={`${percent}% of alert limit`}><i style={{ width: `${percent}%` }} /></span></div>;
}

function PeoplePanel({ users, currentUserId, onEdit }: { users: AdminUser[]; currentUserId: number; onEdit: (user: AdminUser) => void }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const paging = paginate(users, page, pageSize);
  return (
    <section className="admin-panel people-panel" aria-labelledby="people-title">
      <header><div><Users size={17} /><h2 id="people-title">People</h2><span>{users.length}</span></div></header>
      {users.length ? <><div className="admin-table-wrap"><table><caption>Approved user accounts and module access</caption><thead><tr><th>#</th><th>User</th><th>Module access</th><th>Actions</th></tr></thead><tbody>{paging.items.map((item, index) => <tr key={item.id}><td className="admin-row-number">{paging.start + index + 1}</td><td><UserIdentity user={item} badge={item.id === currentUserId ? 'You' : item.reset_requested ? 'Reset requested' : undefined} /></td><td><CapabilitySummary value={capsFromUser(item)} /></td><td><button className="secondary-button admin-edit-user" type="button" onClick={() => onEdit(item)}><UserCog size={14} />Manage</button></td></tr>)}</tbody></table></div><Pagination total={users.length} page={paging.page} pageSize={pageSize} onPage={setPage} onPageSize={value => { setPageSize(value); setPage(0); }} /></> : <div className="admin-panel__empty"><Users size={20} /><span>No approved people yet.</span></div>}
    </section>
  );
}

function EditUserDialog({ user, currentUserId, onClose, onChanged, onStatus }: { user: AdminUser; currentUserId: number; onClose: () => void; onChanged: () => void; onStatus: (status: StatusMessage) => void }) {
  const [caps, setCaps] = useState(() => capsFromUser(user));
  const [password, setPassword] = useState('');
  const save = useMutation({ mutationFn: () => updateAdminUser(user.id, caps), onSuccess: () => { onStatus({ tone: 'ok', text: `${displayName(user)} access saved.` }); onChanged(); }, onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'Access could not be saved.') }) });
  const zoom = useMutation({ mutationFn: () => grantAdminZoom(user.id, !user.zoom_owner_id), onSuccess: result => { onStatus({ tone: 'ok', text: result.granted ? `${displayName(user)} now uses your Zoom.` : `${displayName(user)} now uses their own Zoom.` }); onChanged(); }, onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'Zoom access could not be changed.') }) });
  const resetPassword = useMutation({ mutationFn: () => resetAdminUserPassword(user.id, password), onSuccess: () => { setPassword(''); onStatus({ tone: 'ok', text: `${displayName(user)} password reset.` }); onChanged(); }, onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'Password could not be reset.') }) });
  const remove = useMutation({ mutationFn: () => deleteAdminUser(user.id), onSuccess: () => { onStatus({ tone: 'ok', text: `${displayName(user)} removed.` }); onChanged(); }, onError: error => onStatus({ tone: 'error', text: errorMessage(error, 'The account could not be removed.') }) });
  const isMe = user.id === currentUserId;
  return <Modal title={`Manage ${displayName(user)}`} onClose={onClose}><div className="admin-dialog-identity"><UserIdentity user={user} badge={isMe ? 'You' : undefined} /></div><section className="admin-dialog-section"><h3>Module access</h3><CapabilityGrid value={caps} onChange={setCaps} /><button className="primary-button full-width" type="button" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? <LoaderCircle className="spin" size={14} /> : <Save size={14} />}Save access</button></section>{!isMe ? <section className="admin-dialog-section"><h3>Workspace</h3><button className={`secondary-button full-width ${user.zoom_owner_id ? 'active' : ''}`} type="button" disabled={zoom.isPending} onClick={() => zoom.mutate()}><Link2 size={14} />{user.zoom_owner_id ? 'Using your Zoom · Revoke' : 'Grant use of your Zoom'}</button></section> : null}<section className="admin-dialog-section"><h3>Reset password</h3><div className="admin-password-row"><input type="text" autoComplete="off" placeholder="New temporary password" value={password} onChange={event => setPassword(event.target.value)} /><button className="secondary-button" type="button" disabled={!password || resetPassword.isPending} onClick={() => resetPassword.mutate()}><KeyRound size={14} />Reset</button></div></section>{!isMe ? <section className="admin-dialog-danger"><button className="danger-button" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm(`Remove ${user.username}?`)) remove.mutate(); }}><Trash2 size={14} />Remove account</button></section> : null}</Modal>;
}

function UsageMessageDialog({ user, onClose, onSent }: { user: AdminUsageUser; onClose: () => void; onSent: () => void }) {
  const [body, setBody] = useState(user.over ? `Heads up - you've used $${user.cost.toFixed(2)} of Recall.ai bot credits this cycle.` : '');
  const send = useMutation({ mutationFn: () => sendNotification(body.trim(), user.id), onSuccess: onSent });
  return <Modal title={`Message ${user.name}`} onClose={onClose}><form className="usage-message-form" onSubmit={event => { event.preventDefault(); if (body.trim()) send.mutate(); }}><label><span>Message</span><textarea autoFocus maxLength={2000} rows={5} value={body} onChange={event => setBody(event.target.value)} /></label>{send.isError ? <div className="admin-panel__error"><AlertCircle size={15} /><span>{errorMessage(send.error, 'The message could not be sent.')}</span></div> : null}<div className="admin-dialog-actions"><button className="secondary-button" type="button" onClick={onClose}>Cancel</button><button className="primary-button" type="submit" disabled={!body.trim() || send.isPending}>{send.isPending ? <LoaderCircle className="spin" size={14} /> : <Mail size={14} />}Send message</button></div></form></Modal>;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return <div className="admin-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}><section className="admin-dialog" role="dialog" aria-modal="true" aria-label={title}><header><h2>{title}</h2><button className="icon-button" type="button" title="Close" aria-label="Close" onClick={onClose}><X size={17} /></button></header>{children}</section></div>;
}

function CapabilityGrid({ value, onChange, compact = false }: { value: AdminCapabilityState; onChange: (value: AdminCapabilityState) => void; compact?: boolean }) {
  return <div className={`admin-cap-grid ${compact ? 'compact' : ''}`}>{capabilityOptions.map(option => <label className="admin-checkbox" key={option.key}><input type="checkbox" checked={value[option.key]} onChange={event => onChange({ ...value, [option.key]: event.target.checked })} /><span>{compact ? option.short : option.label}</span></label>)}</div>;
}

function CapabilitySummary({ value }: { value: AdminCapabilityState }) {
  const enabled = capabilityOptions.filter(option => value[option.key]);
  return <div className="admin-cap-summary">{enabled.length ? enabled.map(option => <span className={option.key === 'is_admin' ? 'admin' : ''} key={option.key}>{option.short}</span>) : <span className="empty">No modules</span>}</div>;
}

function UserIdentity({ user, badge }: { user: AdminUser; badge?: string | undefined }) {
  return <div className="admin-user-identity"><strong>{displayName(user)}{badge ? <span className={badge === 'Reset requested' || badge === 'Pending' ? 'admin-warn-badge' : 'admin-user-badge'}>{badge}</span> : null}</strong><span>{user.email || (user.first_name || user.last_name ? user.username : '')}</span></div>;
}

function Pagination({ total, page, pageSize, onPage, onPageSize }: { total: number; page: number; pageSize: number; onPage: (page: number) => void; onPageSize: (size: number) => void }) {
  const start = total ? page * pageSize + 1 : 0;
  const end = Math.min(total, start + pageSize - 1);
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return <div className="admin-pagination"><label><span>Show</span><select value={pageSize} onChange={event => onPageSize(Number(event.target.value))}><option value="10">10</option><option value="25">25</option></select></label><div><span>{start}-{end} of {total}</span><button className="icon-button" type="button" title="Previous page" aria-label="Previous page" disabled={page <= 0} onClick={() => onPage(page - 1)}><ChevronLeft size={16} /></button><button className="icon-button" type="button" title="Next page" aria-label="Next page" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}><ChevronRight size={16} /></button></div></div>;
}

function paginate<T>(items: T[], page: number, pageSize: number) {
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(0, page), pages - 1);
  const start = safePage * pageSize;
  return { page: safePage, start, items: items.slice(start, start + pageSize) };
}

function capsFromUser(user: AdminUser): AdminCapabilityState {
  return Object.fromEntries(capabilityOptions.map(option => [option.key, user[option.key]])) as unknown as AdminCapabilityState;
}

function displayName(user: AdminUser): string {
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;
}

function healthScore(health: AdminHealth | undefined): number {
  const relevant = health?.checks.filter(check => check.status !== 'info') ?? [];
  if (!relevant.length) return 100;
  return Math.round(relevant.reduce((sum, check) => sum + (check.status === 'ok' ? 1 : check.status === 'warn' ? 0.5 : 0), 0) / relevant.length * 100);
}

function formatDateTime(value: number): string {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(value);
}

function inviteText(invite: AdminInvite): string {
  const base = new URL(invite.url, window.location.origin).origin;
  return `Hi! You've been added to ModIQ.\n\nYOUR LOGIN\nSign in here: ${base}${appPath('/login')}\nEmail: ${invite.login}\nTemporary password: ${invite.password}\n\nChange your password and turn on 2FA under Account after signing in.\n\nGET STARTED\nUser guide: ${base}${appPath('/guide')}\nWhat ModIQ does: ${base}${appPath('/highlights')}`;
}
